<template>
  <AdminLayout>
    <v-row class="mb-16 gutters">
      <v-col cols="12" class="mx-xs-auto d-flex flex-column height">
        <coupon-search-and-filter
          class="mb-4"
          :type="'Buchungen'"
          @update:search="searchTerm = $event"
          @update:filters="appliedFilters = $event"
        />
        <v-data-table
          :headers="headers"
          :items="FilteredCoupons"
          :search="search"
          :footer-props="{
            'items-per-page-all-text': 'Alle',
            'items-per-page-text': 'Gutscheine pro Seite',
          }"
          class="bg-accent elevation-1"
          fixed-header
          :loading="loading"
          loading-text="Daten werden geladen..."
        >
          <template #loader>
            <v-progress-linear
              indeterminate
              color="primary"
              height="4"
            ></v-progress-linear>
          </template>
          <template v-slot:item.type="{ item }">
            {{ transformType(item.type) }}
          </template>
          <template v-slot:item.discount="{ item }">
            {{ transformDiscount(item.discount, item.type) }}
          </template>
          <template v-slot:item.maxAmount="{ item }">
            {{ transformAmount(item) }}
          </template>
          <template v-slot:item.validFrom="{ item }">
            {{ transformDate(item.validFrom) }}
          </template>
          <template v-slot:item.validTo="{ item }">
            {{ transformDate(item.validTo) }}
          </template>
          <template v-slot:item.expired="{ item }">
            <v-chip
              variant="flat"
              color="grey-lighten-2"
              v-if="transformExpired(item.validTo, item.validFrom)"
            >
              inaktiv
            </v-chip>
            <v-chip v-else color="secondary" variant="flat"> aktiv </v-chip>
            <v-chip
              v-if="!hasAmount(item.maxAmount, item.usedAmount)"
              color="red"
              class="ml-1 text-black"
              variant="flat"
            >
              Keine Gutscheine mehr verfügbar
            </v-chip>
          </template>
          <template v-slot:item.controls="{ item }">
            <span v-if="item.id !== 'super-admin'">
              <v-menu>
                <template v-slot:activator="{ props }">
                  <v-btn icon variant="text" v-bind="props" size="small">
                    <v-icon>mdi-dots-horizontal</v-icon>
                  </v-btn>
                </template>
                <v-list>
                  <v-list-item
                    link
                    @click="onOpenEditCoupons(item.id)"
                    :disabled="!CouponPermissionService.allowUpdate(item)"
                    prepend-icon="mdi-pencil"
                  >
                    <v-list-item-title>Gutschein bearbeiten</v-list-item-title>
                  </v-list-item>
                  <v-list-item
                    link
                    @click="onOpenDeleteDialog(item.id)"
                    :disabled="!CouponPermissionService.allowDelete(item)"
                    prepend-icon="mdi-delete"
                  >
                    <v-list-item-title>Gutschein löschen</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </span>
          </template>
        </v-data-table>
      </v-col>
    </v-row>
    <v-btn
      color="primary"
      fixed
      size="large"
      rounded
      class="position-fixed bottom-0 right-0 mb-4 mr-5"
      @click="onOpenCreateCoupon"
      :disabled="!CouponPermissionService.allowCreate()"
    >
      <v-icon>mdi-plus</v-icon> Gutschein Erstellen
    </v-btn>
    <CouponEdit
      :coupon="selectedCoupon"
      :open="openEditDialog"
      :mode="openEditDialogMode"
      @close="closeEditDialog"
    ></CouponEdit>
    <CouponDeleteConformationDialog
      :to-delete="selectedCoupon"
      :open="openDeleteDialog"
      @close="onCloseDeleteDialog"
    ></CouponDeleteConformationDialog>
  </AdminLayout>
</template>

<script>
import AdminLayout from "@/layouts/Admin.vue";
import CouponEdit from "../components/Coupon/CouponEdit.vue";
import { mapActions, mapGetters } from "vuex";
import ApiCouponService from "@/services/api/ApiCouponService";
import CouponDeleteConformationDialog from "@/components/Coupon/CouponDeleteConformationDialog.vue";
import CouponPermissionService from "@/services/permissions/CouponPermissionService";
import CouponSearchAndFilter from "@/components/Filters/CouponSearchAndFilter.vue";

export default {
  name: "Coupons",
  components: {
    CouponDeleteConformationDialog,
    CouponEdit,
    AdminLayout,
    CouponSearchAndFilter,
  },
  data() {
    return {
      search: "",
      openEditDialog: false,
      openEditDialogMode: "edit",
      api: {
        coupons: [],
      },
      headers: [
        {
          title: "Gutschein-Nummer",
          value: "id",
        },
        {
          title: "Beschreibung",
          value: "description",
        },
        {
          title: "Rabatt",
          value: "discount",
        },
        {
          title: "Anzahl",
          value: "maxAmount",
        },
        {
          title: "Gültig ab",
          value: "validFrom",
        },
        {
          title: "Gültig bis",
          value: "validTo",
        },
        {
          title: "Status",
          value: "expired",
        },
        { title: "", value: "controls", sortable: false },
      ],
      selectedCoupon: {},
      openDeleteDialog: false,
      appliedFilters: {},
      searchTerm: "",
    };
  },
  computed: {
    ...mapGetters({
      loading: "loading/isLoading",
      tenantId: "tenants/currentTenantId",
    }),
    CouponPermissionService() {
      return CouponPermissionService;
    },
    FilteredCoupons() {
      let coupons = this.api.coupons || [];
      const f = this.appliedFilters;
      const hasFilter =
        this.searchTerm || f?.type || f?.status || f?.validFrom || f?.validTo;

      if (!hasFilter) return coupons;

      return coupons.filter((coupon) => {
        const expired = this.transformExpired(coupon.validTo, coupon.validFrom);

        if (this.searchTerm) {
          const query = this.searchTerm.toLowerCase();
          const matchesSearch =
            (coupon.id && coupon.id.toLowerCase().includes(query)) ||
            (coupon.description &&
              coupon.description.toLowerCase().includes(query));
          if (!matchesSearch) return false;
        }

        if (f.type && coupon.type !== f.type) return false;

        if (f.status) {
          const statusMatch =
            (f.status.toLowerCase() === "aktiv" && !expired) ||
            (f.status.toLowerCase() === "inaktiv" && expired);
          if (!statusMatch) return false;
        }

        if (f.validFrom || f.validTo) {
          const from = f.validFrom ? new Date(f.validFrom) : null;
          const to = f.validTo ? new Date(f.validTo) : null;
          const couponStart = coupon.validFrom
            ? new Date(coupon.validFrom)
            : null;
          const couponEnd = coupon.validTo ? new Date(coupon.validTo) : null;

          if (!couponStart || (from && couponStart < from)) return false;
          if (!couponEnd || (to && couponEnd > to)) return false;
        }

        return true;
      });
    },
  },
  watch: {
    tenantId() {
      this.fetchCoupons();
    },
  },
  methods: {
    ...mapActions({
      startLoading: "loading/start",
      stopLoading: "loading/stop",
    }),
    onOpenDeleteDialog(id) {
      this.selectedCoupon = Object.assign(
        {},
        this.api.coupons.find((coupon) => coupon.id === id)
      );
      this.openDeleteDialog = true;
    },
    onCloseDeleteDialog() {
      this.openDeleteDialog = false;
      this.fetchCoupons();
    },
    fetchCoupons() {
      this.startLoading("fetch-coupons");

      ApiCouponService.getCoupons(undefined)
        .then((response) => {
          this.api.coupons = response.data;
        })
        .finally(() => {
          this.stopLoading("fetch-coupons");
        })
        .catch((error) => {
          console.log(error);
        });
    },
    onOpenCreateCoupon() {
      this.selectedCoupon = { type: "fixed" };
      this.openEditDialog = true;
      this.openEditDialogMode = "create";
    },
    onOpenEditCoupons(id) {
      this.selectedCoupon = Object.assign(
        {},
        this.api.coupons.find((coupon) => coupon.id === id)
      );
      this.openEditDialog = true;
      this.openEditDialogMode = "edit";
    },
    closeEditDialog() {
      this.openEditDialog = false;
      this.fetchCoupons();
    },
    transformDiscount(discount, type) {
      if (type === "percentage") {
        return `${discount}%`;
      }
      return `${discount}€`;
    },
    transformType(type) {
      if (type === "percentage") {
        return "Prozent";
      }
      return "Betrag";
    },
    transformExpired(expirationDate, startDate) {
      const today = new Date();
      const expiration = new Date(expirationDate);
      const start = new Date(startDate);
      if (expirationDate && today > expiration) {
        return true;
      }
      return today < start;
    },
    transformAmount(coupon) {
      if (coupon.maxAmount === undefined || coupon.maxAmount === null) {
        return `${coupon.usedAmount}`;
      }
      return `${coupon.usedAmount}/${coupon.maxAmount}`;
    },
    hasAmount(maxAmount, usedAmount) {
      if (maxAmount === undefined || maxAmount === null) {
        return true;
      }
      return maxAmount > usedAmount;
    },
    transformDate(date) {
      if (!date) {
        return null;
      }
      const dateObj = new Date(date);
      return (
        dateObj.toLocaleDateString("de-DE") +
        " " +
        dateObj.toLocaleTimeString("de-DE")
      );
    },
  },
  created() {
    this.fetchCoupons();
  },
};
</script>

<style scoped></style>
